import dayjs from 'dayjs';
import type {
  TradingRentalEstimation,
  TradingRentalEstimationLine,
  TradingRentalEstimationOverhead,
} from '@/services/tradingRentalEstimation.service';
import { formatRentalCharges, formatRentalDatesOnly } from '@/utils/quotationRentalDisplay';
import { absoluteLetterheadSrc, getCompanyLetterhead } from '@/print-engine/company-letterhead';

export type TradingRentalPrintOpportunity = {
  opportunity_number?: string | null;
  opportunity_name?: string | null;
} | null;

export type BuildTradingRentalEstimationPrintHtmlOptions = {
  lines?: Array<Partial<TradingRentalEstimationLine>>;
  overheads?: Array<Partial<TradingRentalEstimationOverhead>>;
  opportunity?: TradingRentalPrintOpportunity;
  canViewCost?: boolean;
  canViewOverhead?: boolean;
  canViewProfitability?: boolean;
  companyName?: string;
};

function escapeHtml(v: unknown): string {
  return String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function asNum(v: unknown): number {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function fmtMoney(v: unknown, currency?: string): string {
  const n = asNum(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const cur = String(currency || '').trim();
  return cur ? `${n} ${escapeHtml(cur)}` : n;
}

function fmtDate(v: unknown): string {
  if (v == null || v === '') return '—';
  const d = dayjs(v as string);
  return d.isValid() ? d.format('DD MMM YYYY') : escapeHtml(v);
}

function fmtPct(v: unknown): string {
  const n = asNum(v);
  if (!n) return '—';
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
}

function isRentalLine(line: Partial<TradingRentalEstimationLine>): boolean {
  return String(line.line_type || '').toLowerCase() === 'rental';
}

function itemLabel(line: Partial<TradingRentalEstimationLine>): string {
  const code = String(line.item_code || '').trim();
  const name = String(line.item_name || '').trim();
  if (code && name) return `${code} — ${name}`;
  return name || code || '—';
}

function lineDescription(line: Partial<TradingRentalEstimationLine>): string {
  const parts = [
    line.item_description,
    line.specification,
    line.brand ? `Brand: ${line.brand}` : '',
    line.item_category ? `Category: ${line.item_category}` : '',
  ]
    .map((p) => String(p || '').trim())
    .filter(Boolean);
  return parts.join(' · ') || '—';
}

function rentalExtrasHtml(line: Partial<TradingRentalEstimationLine>, currency: string): string {
  const charges = formatRentalCharges(line as any);
  if (charges) return escapeHtml(charges);
  const bits: string[] = [];
  const push = (label: string, amount: unknown) => {
    if (asNum(amount) > 0) bits.push(`${label}: ${fmtMoney(amount, currency)}`);
  };
  push('Security deposit', line.security_deposit);
  push('Mobilization', line.mobilization_charges);
  push('Demobilization', line.demobilization_charges);
  push('Operator', line.operator_charges);
  push('Transport', line.transport_charges);
  push('Damage', line.damage_charges);
  return bits.length ? bits.join('; ') : '—';
}

function buildTradingRows(
  lines: Array<Partial<TradingRentalEstimationLine>>,
  currency: string,
  canViewCost: boolean
): string {
  if (!lines.length) {
    return `<tr><td colspan="${canViewCost ? 11 : 8}" class="muted">No trading lines</td></tr>`;
  }
  return lines
    .map((line, index) => {
      const delivery = line.expected_delivery_date ? fmtDate(line.expected_delivery_date) : '—';
      const stockBits = [
        asNum(line.available_stock) ? `Avail ${asNum(line.available_stock)}` : '',
        asNum(line.balance_required) > 0 ? `Short ${asNum(line.balance_required)}` : '',
      ]
        .filter(Boolean)
        .join(' · ');
      return `<tr>
        <td>${index + 1}</td>
        <td>${escapeHtml(itemLabel(line))}</td>
        <td>${escapeHtml(lineDescription(line))}</td>
        <td class="num">${escapeHtml(asNum(line.quantity))}</td>
        <td>${escapeHtml(line.uom || '')}</td>
        ${
          canViewCost
            ? `<td class="num">${fmtMoney(line.unit_cost, currency)}</td>
               <td class="num">${fmtPct(line.margin_percentage)}</td>
               <td class="num">${fmtMoney(line.cost_amount, currency)}</td>`
            : ''
        }
        <td class="num">${fmtMoney(line.selling_unit_price || line.selling_amount, currency)}</td>
        <td class="num">${fmtMoney(line.selling_amount, currency)}</td>
        <td>${delivery}${stockBits ? `<div class="sub">${escapeHtml(stockBits)}</div>` : ''}${
          line.supplier_preference
            ? `<div class="sub">Supplier: ${escapeHtml(line.supplier_preference)}</div>`
            : ''
        }${line.remarks ? `<div class="sub">${escapeHtml(line.remarks)}</div>` : ''}</td>
      </tr>`;
    })
    .join('');
}

function buildRentalRows(
  lines: Array<Partial<TradingRentalEstimationLine>>,
  currency: string,
  canViewCost: boolean
): string {
  if (!lines.length) {
    return `<tr><td colspan="${canViewCost ? 12 : 11}" class="muted">No rental lines</td></tr>`;
  }
  return lines
    .map((line, index) => {
      const period = String(line.rental_period_type || 'Daily').trim() || 'Daily';
      const duration = asNum(line.rental_duration);
      const dates = formatRentalDatesOnly(line as any);
      return `<tr>
        <td>${index + 1}</td>
        <td>${escapeHtml(itemLabel(line))}</td>
        <td>${escapeHtml(lineDescription(line))}</td>
        <td class="num">${escapeHtml(asNum(line.quantity))}</td>
        <td>${escapeHtml(line.uom || '')}</td>
        <td>${escapeHtml(period)}</td>
        <td class="num">${duration > 0 ? escapeHtml(duration) : '—'}</td>
        <td>${escapeHtml(dates)}</td>
        <td class="num">${fmtMoney(line.rental_rate, currency)}</td>
        <td>${rentalExtrasHtml(line, currency)}${
          line.availability_status
            ? `<div class="sub">Availability: ${escapeHtml(line.availability_status)}</div>`
            : ''
        }${line.remarks ? `<div class="sub">${escapeHtml(line.remarks)}</div>` : ''}</td>
        ${canViewCost ? `<td class="num">${fmtMoney(line.cost_amount, currency)}</td>` : ''}
        <td class="num">${fmtMoney(line.selling_amount || line.total_amount, currency)}</td>
      </tr>`;
    })
    .join('');
}

function buildOverheadRows(
  overheads: Array<Partial<TradingRentalEstimationOverhead>>,
  currency: string
): string {
  if (!overheads.length) {
    return `<tr><td colspan="4" class="muted">No overheads</td></tr>`;
  }
  return overheads
    .map((oh, index) => {
      const amount = oh.base_currency_amount ?? oh.amount ?? oh.original_amount ?? 0;
      return `<tr>
        <td>${index + 1}</td>
        <td>${escapeHtml(oh.overhead_type || '—')}</td>
        <td>${escapeHtml(oh.description || oh.remarks || '—')}</td>
        <td class="num">${fmtMoney(amount, currency)}</td>
      </tr>`;
    })
    .join('');
}

/**
 * Detailed print HTML for Trading/Rental Estimation (Task2 Phase 13).
 * Intended for window.open → document.write → print (do not pass noopener).
 */
export function buildTradingRentalEstimationPrintHtml(
  row: Partial<TradingRentalEstimation> & Record<string, unknown>,
  options: BuildTradingRentalEstimationPrintHtmlOptions = {}
): string {
  const currency = String(row.currency || 'AED');
  const canViewCost = Boolean(options.canViewCost);
  const canViewOverhead = Boolean(options.canViewOverhead ?? options.canViewCost);
  const canViewProfitability = Boolean(options.canViewProfitability ?? options.canViewCost);
  const lines = (options.lines ?? row.lines ?? []) as Array<Partial<TradingRentalEstimationLine>>;
  const overheads = (options.overheads ?? row.overheads ?? []) as Array<
    Partial<TradingRentalEstimationOverhead>
  >;
  const tradingLines = lines.filter((l) => !isRentalLine(l));
  const rentalLines = lines.filter(isRentalLine);
  const opportunity = options.opportunity;
  const companyName = String(options.companyName || 'NTES').trim() || 'NTES';
  const letterheadSrc = absoluteLetterheadSrc(getCompanyLetterhead().headerSrc);
  const projectLabel = [row.project && (row.project as { project_number?: string }).project_number, row.project_name]
    .filter(Boolean)
    .join(' — ');
  const title = String(row.estimation_number || 'Trading/Rental Estimation');

  const tradingSection =
    tradingLines.length > 0 || String(row.estimation_type || '').toLowerCase() !== 'rental services'
      ? `
    <h2>Trading items</h2>
    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>Item</th>
          <th>Description / Specs</th>
          <th class="num">Qty</th>
          <th>UOM</th>
          ${
            canViewCost
              ? '<th class="num">Unit cost</th><th class="num">Margin %</th><th class="num">Cost</th>'
              : ''
          }
          <th class="num">Unit sell</th>
          <th class="num">Amount</th>
          <th>Delivery / notes</th>
        </tr>
      </thead>
      <tbody>${buildTradingRows(tradingLines, currency, canViewCost)}</tbody>
    </table>`
      : '';

  const rentalSection =
    rentalLines.length > 0 || String(row.estimation_type || '').toLowerCase() !== 'industrial trading'
      ? `
    <h2>Rental items / services</h2>
    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>Item</th>
          <th>Description / Specs</th>
          <th class="num">Qty</th>
          <th>UOM</th>
          <th>Period</th>
          <th class="num">Duration</th>
          <th>Dates</th>
          <th class="num">Rate</th>
          <th>Charges / availability</th>
          ${canViewCost ? '<th class="num">Cost</th>' : ''}
          <th class="num">Amount</th>
        </tr>
      </thead>
      <tbody>${buildRentalRows(rentalLines, currency, canViewCost)}</tbody>
    </table>`
      : '';

  const overheadSection = canViewOverhead
    ? `
    <h2>Overheads</h2>
    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>Type</th>
          <th>Description</th>
          <th class="num">Amount</th>
        </tr>
      </thead>
      <tbody>${buildOverheadRows(overheads, currency)}</tbody>
    </table>`
    : '';

  const costSummary =
    canViewCost || canViewProfitability
      ? `
      <div class="summary-block">
        <h3>Internal commercial summary</h3>
        <table class="summary-table">
          <tbody>
            ${canViewCost ? `<tr><td>Total cost</td><td class="num">${fmtMoney(row.total_cost, currency)}</td></tr>` : ''}
            ${canViewOverhead ? `<tr><td>Total overhead</td><td class="num">${fmtMoney(row.total_overhead, currency)}</td></tr>` : ''}
            ${canViewCost ? `<tr><td>Loaded total cost</td><td class="num">${fmtMoney(row.loaded_total_cost ?? row.total_cost, currency)}</td></tr>` : ''}
            ${canViewCost ? `<tr><td>Total margin</td><td class="num">${fmtMoney(row.total_margin, currency)}</td></tr>` : ''}
            ${
              canViewProfitability
                ? `<tr><td>Gross profit</td><td class="num">${fmtMoney(row.gross_profit, currency)} (${fmtPct(
                    row.gross_profit_percentage
                  )})</td></tr>`
                : ''
            }
          </tbody>
        </table>
      </div>`
      : '';

  const terms = String(row.terms_and_conditions || '').trim();
  const remarks = String(row.remarks || '').trim();
  const internalNotes = canViewCost ? String(row.internal_notes || '').trim() : '';

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)}</title>
    <style>
      @page { size: A4; margin: 12mm; }
      * { box-sizing: border-box; }
      body {
        font-family: "Segoe UI", Arial, sans-serif;
        color: #1a1a1a;
        margin: 0;
        padding: 16px 20px 28px;
        font-size: 11px;
        line-height: 1.35;
      }
      .letterhead {
        width: 100%;
        max-height: 72px;
        object-fit: contain;
        object-position: left center;
        margin-bottom: 10px;
      }
      .doc-title { margin: 0 0 2px; font-size: 18px; font-weight: 700; }
      .doc-sub { margin: 0 0 12px; color: #555; font-size: 12px; }
      .meta-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px 24px;
        margin-bottom: 14px;
        border: 1px solid #ddd;
        padding: 10px 12px;
        background: #fafafa;
      }
      .meta-grid .label { color: #666; font-size: 10px; text-transform: uppercase; letter-spacing: 0.03em; }
      .meta-grid .value { font-weight: 600; margin-top: 1px; }
      h2 {
        margin: 16px 0 6px;
        font-size: 13px;
        border-bottom: 2px solid #222;
        padding-bottom: 3px;
      }
      h3 { margin: 0 0 6px; font-size: 12px; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 4px; }
      th, td { border: 1px solid #ccc; padding: 4px 5px; vertical-align: top; }
      th { background: #f0f0f0; text-align: left; font-size: 10px; }
      td.num, th.num { text-align: right; white-space: nowrap; }
      .sub { color: #555; font-size: 10px; margin-top: 2px; }
      .muted { color: #888; font-style: italic; }
      .totals-wrap {
        display: grid;
        grid-template-columns: 1fr 280px;
        gap: 16px;
        margin-top: 14px;
        align-items: start;
      }
      .summary-table td { border: none; border-bottom: 1px solid #eee; padding: 3px 0; }
      .summary-table tr:last-child td { border-bottom: none; font-weight: 700; font-size: 12px; }
      .commercial-box {
        border: 1px solid #ccc;
        padding: 8px 10px;
        background: #fff;
      }
      .commercial-box table { margin: 0; }
      .commercial-box td { border: none; padding: 3px 0; }
      .commercial-box .grand td { font-weight: 700; font-size: 13px; border-top: 1px solid #222; padding-top: 6px; }
      .block {
        margin-top: 14px;
        border: 1px solid #ddd;
        padding: 8px 10px;
        white-space: pre-wrap;
      }
      .block h3 { margin-bottom: 4px; }
      .footer-note {
        margin-top: 18px;
        color: #777;
        font-size: 10px;
        border-top: 1px solid #ddd;
        padding-top: 6px;
      }
      @media print {
        body { padding: 0; }
        .letterhead { max-height: 64px; }
        h2 { break-after: avoid; }
        table { break-inside: auto; }
        tr { break-inside: avoid; }
      }
    </style>
  </head>
  <body>
    <img class="letterhead" src="${escapeHtml(letterheadSrc)}" alt="${escapeHtml(companyName)}" />
    <h1 class="doc-title">Trading / Rental Estimation</h1>
    <p class="doc-sub">${escapeHtml(title)} · ${escapeHtml(row.estimation_type || '—')} · ${escapeHtml(
    row.status || '—'
  )}</p>

    <div class="meta-grid">
      <div>
        <div class="label">Customer</div>
        <div class="value">${escapeHtml(row.customer_name || '—')}</div>
        ${
          row.contact_person
            ? `<div class="sub">Contact: ${escapeHtml(row.contact_person)}</div>`
            : ''
        }
      </div>
      <div>
        <div class="label">Estimation date</div>
        <div class="value">${fmtDate(row.estimation_date)}</div>
      </div>
      <div>
        <div class="label">Opportunity</div>
        <div class="value">${
          opportunity?.opportunity_number || opportunity?.opportunity_name
            ? escapeHtml(
                [opportunity.opportunity_number, opportunity.opportunity_name].filter(Boolean).join(' — ')
              )
            : '—'
        }</div>
      </div>
      <div>
        <div class="label">Project</div>
        <div class="value">${escapeHtml(projectLabel || '—')}</div>
      </div>
      <div>
        <div class="label">Sales team</div>
        <div class="value">${escapeHtml(row.sales_team_name || '—')}</div>
      </div>
      <div>
        <div class="label">Currency / validity</div>
        <div class="value">${escapeHtml(currency)}${
          asNum(row.price_validity_days) > 0
            ? ` · ${escapeHtml(row.price_validity_days)} day(s)`
            : ''
        }</div>
        ${
          row.expected_start_date
            ? `<div class="sub">Expected start: ${fmtDate(row.expected_start_date)}</div>`
            : ''
        }
      </div>
      <div>
        <div class="label">Approval</div>
        <div class="value">${escapeHtml(row.approval_status || '—')}</div>
      </div>
      <div>
        <div class="label">Related quotations</div>
        <div class="value">${escapeHtml(asNum(row.related_quotation_count))}</div>
      </div>
    </div>

    ${tradingSection}
    ${rentalSection}
    ${overheadSection}

    <div class="totals-wrap">
      ${costSummary || '<div></div>'}
      <div class="commercial-box">
        <h3>Commercial summary</h3>
        <table>
          <tbody>
            <tr><td>Before tax</td><td class="num">${fmtMoney(row.total_before_tax, currency)}</td></tr>
            <tr><td>Discount</td><td class="num">${fmtMoney(row.total_discount, currency)}</td></tr>
            <tr><td>Tax / VAT</td><td class="num">${fmtMoney(row.total_tax, currency)}</td></tr>
            <tr class="grand"><td>Grand total</td><td class="num">${fmtMoney(
              row.grand_total,
              currency
            )}</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    ${
      terms
        ? `<div class="block"><h3>Terms and conditions</h3>${escapeHtml(terms)}</div>`
        : ''
    }
    ${remarks ? `<div class="block"><h3>Remarks</h3>${escapeHtml(remarks)}</div>` : ''}
    ${
      internalNotes
        ? `<div class="block"><h3>Internal notes</h3>${escapeHtml(internalNotes)}</div>`
        : ''
    }

    <div class="footer-note">
      Printed ${escapeHtml(dayjs().format('DD MMM YYYY HH:mm'))} · For internal review before formal quotation
    </div>
  </body>
</html>`;
}
